import React from 'react';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';

import { isFeatureEnabled } from 'ff/common/ffAppUtils';
import {
    constructFlightEvents,
    durationSecondsToString,
    formatDistance,
    formatSpeed,
    hasDebriefLicense,
    isSharedPage,
} from 'ff/debrief/utils';
import {
    useCurrentAircraft,
    useCurrentDebrief,
    useCurrentTracklog,
} from 'ff/debrief/hooks';
import ActionRow from 'ff/debrief/components/details/ActionRow';
import FlightEvents from 'ff/debrief/components/details/FlightEvents';
import TerminalDetails from 'ff/debrief/components/details/TerminalDetails';
import MetaDataItem from 'ff/debrief/components/details/MetaDataItem';
import TerminalDetailsWithScore from 'ff/debrief/components/details/TerminalDetailsWithScore';
import TracklogLogbookIcon from 'ff/debrief/components/sidebar/TracklogLogbookIcon';
import { RouteContext } from 'ff/debrief/router';

const TracklogDetails = () => {
    const {
        tracklog: tl,
        trimmedValues: tv,
        currentTracklog: ctl,
        isLoaded,
        hasCloudAhoyDebrief,
    } = useCurrentTracklog();

    const { speedUnit, isLoadingVspeeds } = useCurrentAircraft();
    const { currentDebrief, queryStatus, isCompleted } = useCurrentDebrief();
    const flightEvents = constructFlightEvents(ctl, currentDebrief);
    const { page } = React.useContext(RouteContext);
    const { isScoreModeEnabled } = useCurrentDebrief();

    const showDebrief =
        isFeatureEnabled('debrief_features') &&
        hasDebriefLicense() &&
        hasCloudAhoyDebrief &&
        isScoreModeEnabled &&
        currentDebrief?.score;

    const showLogbook =
        isFeatureEnabled('logbook_association') &&
        tl.logbookAssociationDetails?.length > 0;

    const col1Width = showDebrief ? 4 : 3;
    const col2Width = 12 - col1Width;

    if (!isLoaded) {
        return null;
    }
    return (
        <Grid
            columns={12}
            flexGrow={1}
            padding={2.5}
            columnSpacing={2.5}
            container
        >
            <Grid size={col1Width}>
                {showDebrief ? (
                    <TerminalDetailsWithScore
                        origin={tl?.derivedOrigin}
                        dest={tl?.derivedDestination}
                        start={tv?.timestampStart}
                        end={tv?.timestampEnd}
                        score={
                            tl?.analysisSummary.score || currentDebrief?.score
                        }
                        {...queryStatus}
                    />
                ) : (
                    <TerminalDetails
                        origin={tl?.derivedOrigin}
                        dest={tl?.derivedDestination}
                        start={tv?.timestampStart}
                        end={tv?.timestampEnd}
                    />
                )}
            </Grid>
            <Grid size={col2Width} columns={12} rowGap={3} container>
                <Grid size="grow">
                    <Stack direction="row" spacing={3}>
                        {!isSharedPage(page) && (
                            <MetaDataItem label="Aircraft">
                                {tl?.tailNumber}
                            </MetaDataItem>
                        )}
                        <MetaDataItem label="Total Time">
                            {durationSecondsToString(tv?.totalDurationSeconds)}
                        </MetaDataItem>
                        <MetaDataItem label="Avg Ground Speed">
                            {isLoadingVspeeds
                                ? ''
                                : formatSpeed(tv?.avgGndspd, speedUnit)}
                        </MetaDataItem>
                        <MetaDataItem label="Distance">
                            {formatDistance(tv?.distTotal)}
                        </MetaDataItem>
                    </Stack>
                </Grid>
                <Grid size="auto">
                    <ActionRow isCompleted={isCompleted} />
                </Grid>
                <Grid
                    size={12}
                    container
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid size="auto">
                        <FlightEvents events={flightEvents} />
                    </Grid>
                    <Grid size="auto">
                        {showLogbook && (
                            <TracklogLogbookIcon
                                logbookAssociationDetails={
                                    tl.logbookAssociationDetails
                                }
                                selected={false}
                                logbookId={
                                    tl.logbookAssociationDetails[0]
                                        ?.logbookEntryID
                                }
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default TracklogDetails;
